import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CompanyService } from '@celerofinancas/service-utils';
import {
  BfmDashboardSummaryComponent,
  DASHBOARD_FINANCE_DEFAULT_TOKEN,
  DASHBOARD_FINANCE_GRAPHS_TOKEN,
  DASHBOARD_FINANCE_TOKEN,
  FINANCIAL_STATISTIC,
  PRESENTATION_METHOD,
} from '@celerofinancas/ui-reports';
import { HeaderAccountsComponent } from '@shared/components/header-accounts/header-accounts.component';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { triggerReportsBootstrapWarmup } from '@pages/results/shared/reports-bootstrap-warmup';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { LINKS } from '../../configs/environments-ref';
import {
  buildCaixaDashboardStatisticProperties,
  buildCaixaOnCardRedirect,
} from './dashboard-finance.statistics';

/**
 * dashboard-finance
 */
@Component({
  selector: 'caixa-webcomponent-dashboard-finance',
  standalone: true,
  imports: [
    AsyncPipe,
    BfmDashboardSummaryComponent,
    HeaderAccountsComponent,
    NgxChartsModule,
    MainTitleComponent,
    NavigationBarComponent,
  ],
  providers: [
    {
      provide: DASHBOARD_FINANCE_GRAPHS_TOKEN,
      useValue: {
        customWidth: (parentWidth: number) => parentWidth,
      },
    },
    {
      provide: DASHBOARD_FINANCE_TOKEN,
      useFactory: (company: CompanyService, router: Router) => ({
        statistics: [
          FINANCIAL_STATISTIC.donutRevenue,
          FINANCIAL_STATISTIC.donutExpense,
          FINANCIAL_STATISTIC.groupedLate,
          FINANCIAL_STATISTIC.groupedPredicted,
          FINANCIAL_STATISTIC.barMonthlyComparative,
          FINANCIAL_STATISTIC.groupedExpense,
          FINANCIAL_STATISTIC.groupedRevenue,
          FINANCIAL_STATISTIC.lineDailyBalance,
          FINANCIAL_STATISTIC.sessionCustom,
        ],
        statisticProperties: buildCaixaDashboardStatisticProperties(company, router),
        onCardRedirect: buildCaixaOnCardRedirect(company, router),
      }),
      deps: [CompanyService, Router],
    },
  ],
  templateUrl: './dashboard-finance.component.html',
  styleUrls: ['./dashboard-finance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardFinanceComponent extends BootstrapCoordinator implements OnInit, OnDestroy {

  /**
   * Statistic properties from the dashboard finance token
   */
  public statisticProperties =
    inject(DASHBOARD_FINANCE_TOKEN, { optional: true })?.statisticProperties ??
    DASHBOARD_FINANCE_DEFAULT_TOKEN.statisticProperties;

  /**
   * Lifecycle hook that is called after the component has been initialized.
   */
  public override ngOnInit(): void {
    super.ngOnInit();
    // Acorda o core-registry interceptor para hidratar a empresa ativa.
    // A dash usa bfm-dashboard-summary (ui-reports >= 2.3.x), que só dispara
    // requests depois que CompanyState.getActiveCompanyPk emite um pk. Como
    // este componente não faz nenhuma request HTTP no mount, sem este trigger
    // o interceptor nunca acorda, a empresa nunca hidrata e a dash fica zerada
    // — mesmo deadlock já tratado em /results/monthly e /results/comparative.
    triggerReportsBootstrapWarmup(this.store);
    this.setInformationSessionCustom();
  }

  /**
   * Sets the information for the custom session statistic.
   */
  private setInformationSessionCustom() {
    this.statisticProperties.get(FINANCIAL_STATISTIC.sessionCustom);
    this.statisticProperties?.set(FINANCIAL_STATISTIC.sessionCustom, {
      presentationMethod: PRESENTATION_METHOD.sessionCustom,
      headerTitle: 'Conecte todas as suas contas ao Meu Caixa e tenha mais controle sobre o seu negócio!',
      bodyContent:
        'Centralize as movimentações bancárias da sua empresa e simplifique a gestão financeira.',
      buttonAlign: 'left',
      buttonText: 'Conectar contas',
      backgroundColorHSL: 'hsl(189, 25%, 85%)',
      onClick: () => this.directToOpenFinance(),
    });
  }

  /**
   * Directs the user to the Open Finance link in a new tab.
   */
  private directToOpenFinance() {
    window.open(LINKS.open_finance, '_blank');
  }
}
